import { useSyncExternalStore } from "react";

import { getCurrentSelection, type CurrentSelection } from "./powerpointRunner";
import { subscribeSelection } from "./selectionWatcher";

/**
 * PowerPoint 編集ビュー上の「現在の選択」を React 外部ストアとして公開するフック。
 *
 * - 初回マウント時に `getCurrentSelection()` で現在値を取得
 * - 以降は `subscribeSelection()` で変化を購読
 * - 全コンポーネントで同一の選択状態を共有するため、内部キャッシュをモジュールスコープで保持する
 *
 * テスト容易性のため、`__resetForTest()` で内部状態を初期化できる。
 */

const EMPTY: CurrentSelection = { slideId: null, shapes: [] };

let last: CurrentSelection = EMPTY;
const listeners = new Set<() => void>();
let officeUnsubscribe: (() => void) | null = null;

function selectionsEqual(a: CurrentSelection, b: CurrentSelection): boolean {
    if (a.slideId !== b.slideId) return false;
    if (a.shapes.length !== b.shapes.length) return false;
    for (let i = 0; i < a.shapes.length; i++) {
        if (a.shapes[i].id !== b.shapes[i].id) return false;
    }
    return true;
}

function notify(next: CurrentSelection) {
    if (selectionsEqual(next, last)) return;
    last = next;
    listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
    listeners.add(cb);
    if (listeners.size === 1) {
        officeUnsubscribe = subscribeSelection(notify);
        // 購読が切れているあいだ（画面Bも画面Cも閉じているあいだ）の選択変更は受け取れない。
        // 張り直した時点で現在値を取り直し、離れているうちに動いた選択へ追いつく。
        // 初回だけ取る作りにしていたときは、離れて戻ると前の枚を映したままになっていた。
        void getCurrentSelection().then(notify);
    }
    return () => {
        listeners.delete(cb);
        if (listeners.size === 0 && officeUnsubscribe) {
            officeUnsubscribe();
            officeUnsubscribe = null;
        }
    };
}

function getSnapshot(): CurrentSelection {
    return last;
}

export function useCurrentSelection(): CurrentSelection {
    return useSyncExternalStore(subscribe, getSnapshot);
}

/** テスト用: 内部キャッシュを初期化する。 */
export function __resetUseCurrentSelectionForTest(): void {
    last = EMPTY;
    listeners.clear();
    if (officeUnsubscribe) officeUnsubscribe();
    officeUnsubscribe = null;
}
